import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@vinoga/db";
import { ArrowLeft, Calendar, FolderTree, Globe, Image as ImageIcon, Link as LinkIcon, Power, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground transition-colors";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1.5 block";
const sectionClass =
  "bg-card border border-border rounded-xl p-6 shadow-sm space-y-5";

type LinkType = "category" | "collection" | "custom";

export function BannerEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tr = useT();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [linkType, setLinkType] = useState<LinkType>("custom");
  const [categories, setCategories] = useState<Array<{ id: string; slug: string; name: string }>>([]);
  const [collections, setCollections] = useState<Array<{ id: string; slug: string; name: string }>>([]);
  const [selectedSlug, setSelectedSlug] = useState("");

  const [form, setForm] = useState({
    title: "",
    image_url: "",
    link_url: "",
    alt_text: "",
    placement: params.get("placement") || "home",
    is_active: true,
    date_from: "",
    date_to: "",
    desktop_span: 3,
  });

  const detectLinkType = (url: string): LinkType => {
    if (!url) return "custom";
    if (url.includes("/category/")) return "category";
    if (url.includes("/collections/")) return "collection";
    return "custom";
  };

  useEffect(() => {
    (async () => {
      const [catsRes, colsRes] = await Promise.all([
        supabase
          .from("categories")
          .select("id, slug, translations")
          .eq("active", true)
          .order("position"),
        supabase
          .from("collections")
          .select("id, slug, name")
          .eq("is_active", true)
          .order("sort_order"),
      ]);
      setCategories(
        ((catsRes.data as any[]) ?? []).map((c) => ({
          id: c.id,
          slug: c.slug,
          name: c.translations?.lv?.name ?? c.slug,
        }))
      );
      setCollections(((colsRes.data as any[]) ?? []).map((c) => ({ id: c.id, slug: c.slug, name: c.name })));
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("id", id!)
        .single();
      if (error || !data) {
        toast({
          title: tr("common.notFound", "Not found"),
          variant: "destructive",
        });
        navigate("/banners");
        return;
      }
      const d = data as any;
      const detected = detectLinkType(d.link_url || "");
      setLinkType(detected);
      if (detected !== "custom" && d.link_url) {
        const parts = (d.link_url as string).split("/");
        setSelectedSlug(parts[parts.length - 1] || "");
      }
      setForm({
        title: d.title || "",
        image_url: d.image_url || "",
        link_url: d.link_url || "",
        alt_text: d.alt_text || "",
        placement: d.placement || "home",
        is_active: d.is_active ?? true,
        date_from: d.date_from ? (String(d.date_from).split("T")[0] ?? "") : "",
        date_to: d.date_to ? (String(d.date_to).split("T")[0] ?? "") : "",
        desktop_span: d.desktop_span ?? 3,
      });
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: tr("banners.titleRequired", "Title is required"), variant: "destructive" });
      return;
    }
    if (!form.image_url) {
      toast({ title: tr("banners.imageRequired", "Image URL is required"), variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      image_url: form.image_url,
      link_url: form.link_url.trim() || null,
      alt_text: form.alt_text.trim() || null,
      placement: form.placement.trim() || "home",
      is_active: form.is_active,
      date_from: form.date_from || null,
      date_to: form.date_to || null,
      desktop_span: form.desktop_span,
    };
    try {
      if (isNew) {
        const { error } = await supabase.from("banners").insert(payload);
        if (error) throw error;
        toast({ title: tr("common.created", "Created") });
      } else {
        const { error } = await supabase.from("banners").update(payload).eq("id", id!);
        if (error) throw error;
        toast({ title: tr("common.saved", "Saved") });
      }
      navigate(`/banners`);
    } catch (err: any) {
      toast({
        title: tr("common.saveFailed", "Save failed"),
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {tr("common.loading", "Loading…")}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="sticky top-0 z-10 -mx-4 flex flex-col gap-4 border-b border-border bg-background/85 px-4 py-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:px-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/banners")}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              {isNew
                ? tr("banners.newTitle", "New banner")
                : `${tr("banners.editTitle", "Edit banner")}: ${form.title}`}
            </h1>
            <p className="font-body text-sm text-muted-foreground">
              {tr("banners.editorSubtitle", "Placement-driven promo blocks on the storefront")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/banners")} disabled={saving}>
            {tr("common.cancel", "Cancel")}
          </Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            <Save size={18} />
            {saving
              ? tr("common.saving", "Saving…")
              : isNew
              ? tr("banners.create", "Create banner")
              : tr("common.save", "Save changes")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <ImageIcon className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("banners.sectionDetails", "Details")}
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>
                  {tr("banners.fieldTitle", "Title (internal)")} *
                </label>
                <input
                  className={inputClass}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Pavasara izlase"
                />
              </div>
              <div>
                <label className={labelClass}>
                  {tr("banners.fieldAltText", "Alt text (for accessibility & SEO)")}
                </label>
                <input
                  className={inputClass}
                  value={form.alt_text}
                  onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
                  placeholder={tr("banners.altPlaceholder", "Describe the image")}
                />
              </div>
              <div>
                <label className={labelClass}>{tr("banners.fieldPlacement", "Placement")}</label>
                <input
                  className={inputClass}
                  value={form.placement}
                  onChange={(e) => setForm({ ...form, placement: e.target.value })}
                  list="vg-banner-placements"
                  placeholder="home"
                />
                <datalist id="vg-banner-placements">
                  <option value="home" />
                  <option value="home_lifestyle" />
                  <option value="home_secondary" />
                  <option value="category_top" />
                </datalist>
              </div>
            </div>
          </section>

          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <LinkIcon className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("banners.sectionLink", "Link (optional)")}
              </h2>
            </div>

            <div>
              <label className={labelClass}>{tr("banners.fieldLinkType", "Link type")}</label>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { key: "category", label: tr("banners.linkCategory", "Category"), icon: FolderTree },
                    { key: "collection", label: tr("banners.linkCollection", "Collection"), icon: Sparkles },
                    { key: "custom", label: tr("banners.linkCustom", "Custom URL"), icon: Globe },
                  ] as { key: LinkType; label: string; icon: any }[]
                ).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setLinkType(key);
                      setSelectedSlug("");
                      setForm((f) => ({ ...f, link_url: "" }));
                    }}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs font-medium transition-colors ${
                      linkType === key
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {linkType === "category" && (
              <div>
                <label className={labelClass}>{tr("banners.pickCategory", "Choose a category")}</label>
                <select
                  className={inputClass}
                  value={selectedSlug}
                  onChange={(e) => {
                    const slug = e.target.value;
                    setSelectedSlug(slug);
                    setForm((f) => ({ ...f, link_url: slug ? `/category/${slug}` : "" }));
                  }}
                >
                  <option value="">— {tr("banners.selectOne", "select one")} —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {linkType === "collection" && (
              <div>
                <label className={labelClass}>{tr("banners.pickCollection", "Choose a collection")}</label>
                <select
                  className={inputClass}
                  value={selectedSlug}
                  onChange={(e) => {
                    const slug = e.target.value;
                    setSelectedSlug(slug);
                    setForm((f) => ({ ...f, link_url: slug ? `/collections/${slug}` : "" }));
                  }}
                >
                  <option value="">— {tr("banners.selectOne", "select one")} —</option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {linkType === "custom" && (
              <div>
                <label className={labelClass}>{tr("banners.fieldCustomUrl", "Custom URL")}</label>
                <input
                  className={inputClass}
                  value={form.link_url}
                  onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                  placeholder="/category/sarkanvins"
                />
              </div>
            )}

            {form.link_url && (
              <p className="text-xs text-muted-foreground">
                {tr("banners.resolvedUrl", "Resolved URL:")}{" "}
                <span className="font-mono text-foreground">{form.link_url}</span>
              </p>
            )}
          </section>

          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <Calendar className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("banners.sectionAvailability", "Availability")}
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>{tr("banners.fieldDateFrom", "Show from")}</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.date_from}
                  onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                />
              </div>
              <div>
                <label className={labelClass}>{tr("banners.fieldDateTo", "Show until")}</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.date_to}
                  onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-3">
                <div
                  className={`rounded-full p-2 ${
                    form.is_active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  <Power size={18} />
                </div>
                <div>
                  <p className="font-body text-sm font-medium text-foreground">
                    {tr("banners.fieldActive", "Active")}
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    {tr("banners.activeHint", "Show this banner on the storefront")}
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="size-4 cursor-pointer rounded-sm"
              />
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className={sectionClass}>
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <ImageIcon className="text-primary" size={18} />
              <h2 className="font-display text-base font-semibold">
                {tr("banners.sectionImage", "Image")} *
              </h2>
            </div>

            <div>
              <label className={labelClass}>{tr("banners.fieldDesktopSpan", "Desktop width")}</label>
              <select
                className={inputClass}
                value={form.desktop_span}
                onChange={(e) => setForm({ ...form, desktop_span: parseInt(e.target.value) })}
              >
                <option value={1}>1 col (1:1, ~460×460) — mobile + desktop</option>
                <option value={2}>2 col (16:9, ~920×520) — desktop only</option>
                <option value={3}>3 col (21:9, ~1380×590) — desktop only</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                {tr(
                  "banners.spanHint",
                  "Small (1 col) banners show on mobile in a swipable slider; wide (2–3 col) banners are shown only on desktop."
                )}
              </p>
            </div>

            <div>
              <label className={labelClass}>{tr("banners.fieldImage", "Image")} *</label>
              <ImageUpload
                value={form.image_url}
                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                bucket="banners"
                preserveAspectRatio
                placeholder={tr("banners.uploadHint", "Drop a hero image here (or click to browse)")}
              />
              <p className="mt-2 text-[11px] text-muted-foreground">
                {tr(
                  "banners.uploadFormat",
                  "Banner images keep their natural aspect ratio. Recommended ≥ 2000 px wide for crispness on Retina.",
                )}
              </p>
              {form.image_url && (
                <details className="mt-3">
                  <summary className="text-[11px] text-muted-foreground cursor-pointer">
                    {tr("banners.showUrl", "Show / paste raw URL")}
                  </summary>
                  <input
                    className={inputClass + " font-mono text-xs mt-2"}
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  />
                </details>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
